import React from "react";
import ValuesRow from "@/components/ValuesRow";

const values = [
  { title: "Without judgement", body: "We work with people from all backgrounds and experiences without judgement, and recognise that every story is different." },
  { title: "Strength, not deficit", body: "Our focus is not on defining people by their past but on recognising their strengths and removing barriers." },
  { title: "Family included", body: "Justice involvement affects entire families. We support partners, parents, children and carers as part of the same work." },
  { title: "Lived experience", body: "Alongside qualified social workers, our team includes peer workers with lived experience of the system." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="fade-in">
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-12">
        <div className="njas-eyebrow">About Us</div>
        <div className="njas-script text-5xl mt-6" style={{ color: "#12283F" }}>Founded by</div>
        <h1 className="njas-display text-6xl sm:text-8xl lg:text-9xl text-njas-navy mt-2 leading-[0.88] max-w-5xl">
          SOCIAL&nbsp;WORKERS.
        </h1>
        <div className="njas-script text-5xl mt-6" style={{ color: "#4E5F49" }}>Guided by</div>
        <h1 className="njas-display text-6xl sm:text-8xl lg:text-9xl mt-2 leading-[0.88] max-w-5xl text-njas-sage-deep">
          LIVED&nbsp;EXPERIENCE.
        </h1>

        <p className="mt-10 max-w-3xl text-lg text-njas-muted leading-relaxed">
          Not Just A Statistic was founded by qualified social workers with more than 12 years combined
          experience across the human services sector. Our work is grounded in evidence and professional
          practice, but it is the people we walk alongside who guide it.
        </p>

        <div className="mt-10 max-w-3xl pl-6 border-l-2 border-njas-sage" data-testid="experts-pullquote">
          <p className="njas-display text-2xl sm:text-3xl text-njas-navy leading-tight">
            Our clients are the experts of their own lives.
          </p>
          <p className="mt-3 njas-script text-3xl text-njas-sage-deep">
            We take our lead from those who know their story best.
          </p>
        </div>
      </section>

      <section className="bg-[var(--njas-bg-alt)] border-y border-[var(--njas-line)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16">
          <ValuesRow />
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="njas-eyebrow">Our Story</div>
            <h2 className="njas-display text-5xl sm:text-6xl text-njas-navy mt-4 leading-[0.9]">
              CHANGE&nbsp;EXTENDS<br/>BEYOND&nbsp;THE<br/><span className="text-njas-sage-deep">INDIVIDUAL.</span>
            </h2>
            <div className="mt-10 njas-script text-4xl text-njas-sage-deep">Mentoring young people</div>
            <div className="njas-display text-3xl text-njas-navy leading-tight mt-1">
              before the system does.
            </div>
            <div className="mt-8 flex items-center gap-3">
              <span className="w-10 h-px bg-njas-sage" />
              <span className="njas-condensed tracking-[0.3em] text-[10px] font-semibold text-njas-sage-deep">Early Intervention</span>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-[var(--njas-sage)]/40 space-y-6 text-njas-text leading-relaxed">
            <p>
              We recognise that lasting change extends beyond the individual. By supporting families,
              carers and loved ones, we strengthen relationships, promote stability and help create
              the foundation for successful reintegration, healthier communities and brighter futures.
            </p>
            <p>
              For many young people at risk of justice involvement, an early relationship with a
              consistent, non-judgemental adult can change the trajectory of their life. Our early
              intervention work is built around mentoring, life skills, education and employment
              support, and family connection, before things escalate.
            </p>
            <p>
              We know that for many people, the justice system does not feel built for them. Our role
              is to help you understand it, navigate it, and ensure you never have to face it alone.
            </p>
            <p>
              Alongside qualified social workers, our team includes peer workers with lived experience
              of the justice system. Some of our peer workers first connected with us through our
              support services and now choose to give back by supporting others facing similar challenges.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-24">
        <div className="njas-eyebrow mb-8">What Guides Us</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-[var(--njas-sage)]/50">
          {values.map((v, i) => (
            <div key={v.title} className={`p-8 border-b border-[var(--njas-sage)]/50 ${i < values.length - 1 ? "md:border-r" : ""}`} data-testid={`value-card-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="njas-display text-4xl text-njas-navy leading-tight">{v.title}</div>
              <p className="text-sm text-njas-muted mt-4 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-njas-navy text-njas-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="njas-script text-5xl text-njas-sage">A promise</div>
          <p className="mt-6 njas-display text-3xl sm:text-4xl leading-tight">
            "NO&nbsp;MATTER WHERE&nbsp;YOU'VE&nbsp;BEEN OR&nbsp;WHAT&nbsp;YOU'VE&nbsp;EXPERIENCED, YOU'LL&nbsp;BE&nbsp;MET&nbsp;WITHOUT JUDGEMENT."
          </p>
          <p className="mt-6 text-njas-bg/70 max-w-xl mx-auto text-sm">
            With honesty, respect, compassion, and a genuine commitment to helping you move forward.
          </p>
        </div>
      </section>
    </div>
  );
}
